import type { SearchEntry } from '../../redux/features/savedSearchesSlice';
import type { SavedSearchSummary } from '../../types/saved-search';
import { parseSavedSearchUrlToSummary } from '../saved-searches/parse-saved-search-summary';
import { searchEntryRecencyWeight } from './search-entry-recency-weight';

export const MAX_WEIGHTED_SEARCHES_FOR_INFERENCE = 25;

export type PreparedWeightedSearchSummary = {
  w: number;
  summary: SavedSearchSummary;
};

function hasUsableSummary(summary: SavedSearchSummary): boolean {
  const q = summary.query?.trim();
  if (q && q.length >= 2) return true;
  if (summary.locationValues.length > 0) return true;
  if (summary.facets.some((f) => f.values.length > 0)) return true;
  return false;
}

/**
 * Parses recent search URLs into recency-weighted summaries (newest / freshest searches weigh more).
 * Shared by job match inference and profile prefill from searches.
 */
export function prepareWeightedSearchSummariesForInference(
  entries: SearchEntry[],
  options?: { nowMs?: number; maxSearches?: number }
): PreparedWeightedSearchSummary[] | null {
  if (!entries?.length) return null;
  const nowMs = options?.nowMs ?? Date.now();
  const max = options?.maxSearches ?? MAX_WEIGHTED_SEARCHES_FOR_INFERENCE;

  const ordered = [...entries].sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0)).slice(0, max);

  const prepared: PreparedWeightedSearchSummary[] = [];
  let usableRank = 0;
  for (const e of ordered) {
    if (!e?.url) continue;
    const summary = parseSavedSearchUrlToSummary(e.url);
    if (!hasUsableSummary(summary)) continue;
    const w = searchEntryRecencyWeight(usableRank, e.timestamp, nowMs);
    prepared.push({ w, summary });
    usableRank += 1;
  }

  return prepared.length > 0 ? prepared : null;
}
